// src/pages/Transaction.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveAuthRole, getActiveToken } from '../utils/authStorage';
import {
  FaFilter,
  FaSearch,
  FaCreditCard,
  FaCircle,
  FaCheck,
  FaTimes,
  FaSync,
  FaReceipt,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaBroadcastTower,
  FaDownload,
  FaFileExcel,
  FaChevronDown,
  FaClock,
  FaBan,
  FaHistory,
  FaArchive,
  FaArrowLeft,
  FaTrash,
} from 'react-icons/fa';
import { formatCodeWithFrench, getFrenchMessageForCode } from './responseCodes';
import * as XLSX from 'xlsx-js-style';

const API_BASE = process.env.REACT_APP_PAYMENT_SERVICE_URL;
const STORE_API = process.env.REACT_APP_STORE_SERVICE_URL || 'http://localhost:3006';

export default function Transaction() {
  const currentRole = getActiveAuthRole();
  const canFilterByMerchant = currentRole === 'admin' || currentRole === 'finance';
  const canViewArchivedTransactions = currentRole === 'admin';
  // --- state ---
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1); // server page
  const [limit, setLimit] = useState(50); // backend page size
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedHistoryTransaction, setSelectedHistoryTransaction] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [expandedAttemptResults, setExpandedAttemptResults] = useState({});
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedArchiveIds, setSelectedArchiveIds] = useState([]);
  const exportMenuRef = useRef(null);

  // stats alimentées par GET /transactions → statistics.byResult (+ total)
  const [globalSummary, setGlobalSummary] = useState({ total: 0, approved: 0, rejected: 0, timeouts: 0, cancledseul: 0, tpe: 0 });

  // stores & caisses for dropdowns
  const [stores, setStores] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [merchants, setMerchants] = useState([]);

  // filters — caisseId = device._id (MongoDB), storeId = store._id (MongoDB)
  // updatedFrom / updatedTo : YYYY-MM-DD → query updatedFrom & updatedTo (filtre sur updatedAt côté API)
  const [filters, setFilters] = useState({
    caisseId: '',
    clientId: '',
    merchantId: '',
    storeId: '',
    storeCode: '',
    status: '',
    updatedFrom: '',
    updatedTo: '',
  });
  const [filterInputs, setFilterInputs] = useState({
    caisseId: '',
    merchantId: '',
    storeId: '',
    storeCode: '',
    status: '',
    updatedFrom: '',
    updatedTo: '',
  });

  // --- refs ---
  const componentMountedRef = useRef(true);
  const currentPageRef = useRef(currentPage);
  const limitRef = useRef(limit);
  const filtersRef = useRef(filters);
  const archivedOnlyRef = useRef(false);

  // dynamic height measurement
  const topAreaRef = useRef(null);
  const bottomAreaRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState('calc(100vh - 240px)');

  // keep refs in sync
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { limitRef.current = limit; }, [limit]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { archivedOnlyRef.current = archivedOnly; }, [archivedOnly]);
  useEffect(() => {
    if (!archivedOnly) setSelectedArchiveIds([]);
  }, [archivedOnly]);

  const getToken = () => getActiveToken() || null;
  const getAuthHeaders = () => {
    const token = getToken();
    if (!token) return null;
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  // --- Status labels (UI) ---
  const STATUS_OPTIONS = [
    'Transaction Approved',
    'Transaction Rejected',
    'Transaction Canceled',
    'TPE Unavailable',
    'Timeout'
  ];

  // Pour le filtre client-side : correspond aux valeurs retournées par getTransactionStatus()
  const labelToStatusKey = {
    'Transaction Approved': 'approved',
    'Transaction Rejected': 'rejected',
    'Transaction Canceled': 'canceled',
    'TPE Unavailable': 'tpe-unavailable',
    'Timeout': 'timeout',
  };

  // Pour le filtre serveur (/transactions) : valeurs exactes stockées en DB
  const labelToDbStatus = {
    'Transaction Approved': 'approved',
    'Transaction Rejected': 'rejected',
    'Transaction Canceled': 'cancledseul',
    'TPE Unavailable': 'tpe',
    'Timeout': 'timeouts',
  };

  /** KPI depuis la réponse GET /transactions : statistics.byResult (+ total) */
  const applyStatsFromTransactionsResponse = (data) => {
    const stats = data?.statistics;
    const br = stats?.byResult;
    const num = (v) => (Number(v) || 0);
    const total =
      stats?.total != null && stats.total !== ''
        ? num(stats.total)
        : num(data?.total);
    if (br && typeof br === 'object') {
      setGlobalSummary({
        total: total || 0,
        approved: num(br.approved),
        rejected: num(br.rejected),
        timeouts: num(br.timeouts),
        cancledseul: num(br.cancledseul),
        tpe: num(br.tpe),
      });
      return;
    }
    if (total > 0) {
      setGlobalSummary((prev) => ({ ...prev, total }));
    }
  };

  // ── Fetch stores (pour dropdown Magasin) ────────────────────────────────
  const fetchStores = async () => {
    try {
      const headers = getAuthHeaders();
      const fetchOptions = headers
        ? { headers }
        : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores`, fetchOptions);
      if (!componentMountedRef.current || !res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data.stores) ? data.stores : (Array.isArray(data) ? data : []);
      const sorted = list.slice().sort((a, b) =>
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      if (componentMountedRef.current) setStores(sorted);
    } catch (err) {
      console.error('fetchStores error:', err);
    }
  };

  // ── Fetch caisses (pour dropdown Caisse) ────────────────────────────────
  const fetchCaisses = async () => {
    try {
      const headers = getAuthHeaders();
      const fetchOptions = headers
        ? { headers }
        : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
      const res = await fetch(
        `${API_BASE}/devices?clientType=CAISSE&includePairing=true&page=1&limit=1000&sortBy=createdAt&sortOrder=desc`,
        fetchOptions
      );
      if (!componentMountedRef.current || !res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data.devices) ? data.devices : [];
      if (componentMountedRef.current) setCaisses(list);
    } catch (err) {
      console.error('fetchCaisses error:', err);
    }
  };

  const fetchMerchantOptions = async () => {
    if (!canFilterByMerchant) {
      if (componentMountedRef.current) setMerchants([]);
      return;
    }
    try {
      const headers = getAuthHeaders();
      const fetchOptions = headers
        ? { headers }
        : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
      const res = await fetch(`${API_BASE}/merchants`, fetchOptions);
      if (!componentMountedRef.current || !res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data.merchants) ? data.merchants : (Array.isArray(data) ? data : []);
      const normalized = list.map((m) => ({
        ...m,
        id: m?.id || m?._id,
        displayName:
          m?.userFullName ||
          `${m?.user?.name || m?.userName || ''} ${m?.user?.lastName || m?.userLastName || ''}`.trim() ||
          m?.email ||
          m?.id ||
          '',
      }));
      if (componentMountedRef.current) setMerchants(normalized);
    } catch (err) {
      console.error('fetchMerchantOptions error:', err);
    }
  };

  const loadLiveFilterOptions = () => {
    fetchStores();
    fetchCaisses();
    fetchMerchantOptions();
  };

  const fetchArchiveFilterOptions = async () => {
    try {
      const headers = getAuthHeaders();
      const fetchOptions = headers
        ? { headers }
        : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
      const base = String(API_BASE || '').replace(/\/$/, '');
      const res = await fetch(`${base}/metrics/dashboard/filters?archivedOnly=true`, fetchOptions);
      if (!componentMountedRef.current || !res.ok) return;
      const data = await res.json();
      const payload = data?.filters && typeof data.filters === 'object' ? data.filters : data;

      const rawStores = Array.isArray(payload.stores) ? payload.stores : [];
      const mappedStores = rawStores.map((s) => {
        const code = s.code || s.storeCode || '';
        const id = s._id || s.id || code;
        return {
          ...s,
          _id: id,
          id,
          code,
          name: s.name || s.storeName || code,
          merchantId: s.merchantId,
        };
      });

      const rawCaisses = Array.isArray(payload.caisses) ? payload.caisses : [];
      const mappedCaisses = rawCaisses.map((c, i) => {
        const clientId = c.clientId || c.caisseName || c.name || '';
        const id = c._id || c.id || clientId || `caisse-${i}`;
        return {
          ...c,
          _id: id,
          clientId,
          name: c.name || c.caisseName || clientId,
        };
      });

      const rawMerchants = Array.isArray(payload.merchants) ? payload.merchants : [];
      let mappedMerchants = rawMerchants.map((m) => ({
        ...m,
        id: m?.id || m?._id,
        displayName:
          m?.userFullName ||
          m?.displayName ||
          `${m?.user?.name || m?.userName || ''} ${m?.user?.lastName || m?.userLastName || ''}`.trim() ||
          m?.name ||
          m?.email ||
          m?.id ||
          '',
      }));

      if (!mappedMerchants.length) {
        const seen = new Map();
        mappedStores.forEach((s) => {
          const merchant = s?.merchantId;
          const mid = merchant && typeof merchant === 'object'
            ? (merchant.id || merchant._id || '')
            : (merchant || '');
          if (!mid || seen.has(String(mid))) return;
          seen.set(String(mid), {
            id: mid,
            displayName:
              (merchant && typeof merchant === 'object'
                ? (merchant.userFullName || merchant.name || merchant.email)
                : '') || String(mid),
          });
        });
        mappedMerchants = [...seen.values()];
      }

      if (!componentMountedRef.current) return;
      setStores(mappedStores);
      setCaisses(mappedCaisses);
      setMerchants(canFilterByMerchant ? mappedMerchants : []);
    } catch (err) {
      console.error('fetchArchiveFilterOptions error:', err);
    }
  };

  // fetch transactions (server pagination). Les filtres magasin / statut / dates partent au serveur.
  const fetchTransactions = (page = currentPageRef.current, limitParam = null, filtersParam = null) => {
    setLoading(true);
    setError(null);
    const headers = getAuthHeaders();
    const fetchOptions = headers ? { headers } : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
    const effectiveLimit = limitParam != null ? limitParam : limitRef.current;
    const effectiveFilters = filtersParam || filtersRef.current || {};
    const params = new URLSearchParams({ page: page.toString(), limit: effectiveLimit.toString() });
    // Pour la table on passe clientId (champ métier du device), pas le MongoDB _id
    if (effectiveFilters.clientId) params.append('clientId', effectiveFilters.clientId);
    if (effectiveFilters.merchantId) params.append('merchantId', effectiveFilters.merchantId);
    if (effectiveFilters.storeId) params.append('storeId', effectiveFilters.storeId);
    if (effectiveFilters.storeCode) params.append('storeCode', effectiveFilters.storeCode);
    // status envoyé au serveur avec la valeur DB exacte → filtre sur toutes les pages
    if (effectiveFilters.statusKey) params.append('status', effectiveFilters.statusKey);
    const uf = (effectiveFilters.updatedFrom || '').toString().trim();
    const ut = (effectiveFilters.updatedTo || '').toString().trim();
    if (uf) params.append('updatedFrom', uf);
    if (ut) params.append('updatedTo', ut);
    if (archivedOnlyRef.current) params.append('archivedOnly', 'true');

    fetch(`${API_BASE}/transactions?${params.toString()}`, fetchOptions)
      .then(async res => {
        if (!componentMountedRef.current) return;
        if (!res.ok) throw new Error(await res.text() || 'Erreur récupération');
        return res.json();
      })
      .then(data => {
        if (!componentMountedRef.current) return;
        if (data.success && Array.isArray(data.transactions)) {
          setTransactions(data.transactions);
          setTotalCount(data.total || 0);
          setTotalPages(data.totalPages || 0);
          setCurrentPage(data.page || page);
          currentPageRef.current = data.page || page;
          applyStatsFromTransactionsResponse(data);
        } else {
          console.error('Unexpected API response structure:', data);
          setTransactions([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      })
      .catch(err => {
        if (!componentMountedRef.current) return;
        console.error('Fetch error:', err);
        setError(err.message || 'Erreur réseau');
        setTransactions([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!componentMountedRef.current) return;
        setLoading(false);
      });
  };

  // --- dynamic height computation for visible page ---
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const compute = () => {
      try {
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const topRect = topAreaRef.current ? topAreaRef.current.getBoundingClientRect() : { height: 240 };
        const bottomRect = bottomAreaRef.current ? bottomAreaRef.current.getBoundingClientRect() : { height: 48 };
        const extra = 32;
        let avail = Math.max(200, Math.floor(vh - topRect.height - bottomRect.height - extra));
        avail = Math.min(avail, vh - 100);
        setTableMaxHeight(`${avail}px`);
      } catch (e) {
        setTableMaxHeight('calc(100vh - 240px)');
      }
    };
    compute();
    window.addEventListener('resize', compute);
    let roTop = null;
    let roBottom = null;
    if (window.ResizeObserver) {
      roTop = new ResizeObserver(() => compute());
      roBottom = new ResizeObserver(() => compute());
      if (topAreaRef.current) roTop.observe(topAreaRef.current);
      if (bottomAreaRef.current) roBottom.observe(bottomAreaRef.current);
    }
    return () => {
      window.removeEventListener('resize', compute);
      if (roTop) roTop.disconnect();
      if (roBottom) roBottom.disconnect();
    };
  }, []);

  // --- lifecycle ---
  useEffect(() => {
    componentMountedRef.current = true;
    currentPageRef.current = 1;
    fetchTransactions(1, limitRef.current, {});
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (archivedOnly) fetchArchiveFilterOptions();
    else loadLiveFilterOptions();
  }, [archivedOnly]);

  useEffect(() => {
    fetchTransactions(currentPage, limit, filters);
  }, [currentPage, filters, limit, archivedOnly]);

  // --- helpers for display & result parsing ---
  const getStoreCode = trans => (trans?.storeId && trans.storeId.code) ? trans.storeId.code : (trans.storeCode || '—');
  const getResultCode = trans => {
    const res = trans?.result;
    if (!res) return '—';
    const candidates = [res.code, res.responseCode, res.response?.code, res.code_retour, res.codeRetour, res.statusCode, res.response_code, res.returnCode, res.return_code];
    for (const c of candidates) {
      if (c !== undefined && c !== null && c !== '') return String(c);
    }
    if (typeof res === 'string') {
      const m = res.match(/(?:code[:=]\s*)([0-9A-Za-z-]+)/i);
      if (m) return m[1];
      return res;
    }
    try {
      const json = JSON.stringify(res);
      const m = json.match(/"code"\s*[:=]\s*"?([0-9A-Za-z-]+)"?/i);
      if (m) return m[1];
    } catch (e) { }
    return '—';
  };

  const isApprovedTransaction = (trans) => {
    if (!trans) return false;
    const res = trans?.result;
    if (!res) return false;
    if (typeof res === 'string' && res.trim().toLowerCase() === 'approved') return true;
    if (typeof res === 'object') {
      const keys = Object.keys(res);
      for (const k of keys) {
        const v = res[k];
        if ((typeof v === 'string' && v.trim().toLowerCase() === 'approved') || String(v).trim().toLowerCase() === 'approved') return true;
      }
    }
    return false;
  };

  const isApprovedForCode = (trans) => {
    if (!trans) return false;
    const res = trans?.result;
    if (!res) return false;
    if (typeof res === 'string') {
      return res.trim().toUpperCase() === 'TRANSACTION APPROVED';
    }
    if (typeof res === 'object') {
      const inner = res.result;
      if (typeof inner === 'string' && inner.trim().toUpperCase() === 'TRANSACTION APPROVED') return true;
    }
    return false;
  };

  const getFilteredResultFields = result => {
    if (!result || typeof result !== 'object') return {};
    const filtered = { ...result };
    delete filtered.type;
    delete filtered.sessionId;
    return filtered;
  };

  const getPrimaryResultField = (result) => {
    if (!result || typeof result !== 'object') return null;
    const keys = Object.keys(result);
    if (keys.length === 0) return null;
    let key = keys.find(k => k.toLowerCase() === 'result');
    if (!key) key = keys.find(k => k.toLowerCase().includes('message'));
    if (!key) key = keys.find(k => k.toLowerCase().includes('response'));
    if (!key) key = keys[0];
    return { key, value: result[key] };
  };

  const formatResultDisplay = (raw) => {
    if (raw === null || raw === undefined) return '—';
    let s;
    if (typeof raw === 'object') {
      try { s = JSON.stringify(raw); } catch { s = String(raw); }
    } else { s = String(raw); }
    s = s.replace(/_/g, ' ').trim().replace(/\s+/g, ' ').toLowerCase();
    s = s.split(' ').map(word => word.split('-').map(part => part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part).join('-')).join(' ');
    return s === '' ? '—' : s;
  };

  const formatDateSimple = (timestamp) => {
    if (!timestamp) return '—';
    // Parse ISO string directly without timezone conversion
    const match = String(timestamp).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return '—';
    const [, year, month, day, hour, minute] = match;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  const formatAmount = (amount, currency = 'TND') => {
    if (!Number.isFinite(Number(amount))) return '—';
    return `${new Intl.NumberFormat('fr-TN', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Number(amount) / 1000)} ${currency || 'TND'}`;
  };

  const getHistoryPayload = (history, fallbackTransaction = null) => {
    const source = history && typeof history === 'object' ? history : (fallbackTransaction || {});
    const attempts = Array.isArray(source.attempts)
      ? source.attempts
        .slice()
        .sort((a, b) => (Number(a?.attemptNumber) || 0) - (Number(b?.attemptNumber) || 0))
      : [];

    return {
      transactionId: source.transactionId || fallbackTransaction?.transactionId || fallbackTransaction?._id || '—',
      clientId: source.clientId || fallbackTransaction?.clientId || '—',
      amount: source.amount ?? fallbackTransaction?.amount,
      currency: source.currency || fallbackTransaction?.currency || 'TND',
      statusSource: source,
      attemptCount: source.attemptCount ?? attempts.length ?? 0,
      currentAttemptNumber: source.currentAttemptNumber ?? attempts[attempts.length - 1]?.attemptNumber,
      lastAttemptAt: source.lastAttemptAt || attempts[attempts.length - 1]?.completedAt || attempts[attempts.length - 1]?.updatedAt,
      result: source.result,
      attempts,
    };
  };

  const getResultFieldsForDisplay = (result, fallbackAmount = null) => {
    const res = result && typeof result === 'object' ? result : {};
    const fieldsToDisplay = [
      { key: 'responseCode', label: 'Response Code' },
      { key: 'result', label: 'Result' },
      { key: 'cardNo', label: 'Card No' },
      { key: 'authCode', label: 'Auth Code' },
      { key: 'terminalId', label: 'Terminal ID' },
      { key: 'amount', label: 'Amount' },
      { key: 'dateTime', label: 'Date Time' },
      { key: 'Aid', label: 'AID' },
      { key: 'ARQC', label: 'ARQC' },
      { key: 'TVR', label: 'TVR' },
      { key: 'TSI', label: 'TSI' },
      { key: 'appLabel', label: 'App Label' },
      { key: 'typeTrans', label: 'Type Trans' },
      { key: 'transactionId', label: 'Transaction ID' },
    ];

    return fieldsToDisplay.map(({ key, label }) => {
      let value = res[key];
      let hasValue = value !== undefined && value !== null && value !== '';

      if (key === 'amount' && !hasValue && fallbackAmount !== undefined && fallbackAmount !== null && fallbackAmount !== '') {
        value = fallbackAmount;
        hasValue = true;
      }

      let displayValue = '---';
      if (hasValue) {
        if (key === 'responseCode') {
          const codeStr = String(value);
          const frenchMsg = getFrenchMessageForCode(codeStr);
          displayValue = frenchMsg && !codeStr.includes(' - ') ? `${codeStr} - ${frenchMsg}` : codeStr;
        } else if (key === 'amount') {
          displayValue = formatAmount(value);
        } else {
          displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        }
      }

      return {
        key,
        label,
        displayValue,
        isWide: key === 'responseCode',
      };
    });
  };

  const copyToClipboard = async text => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  // --- helper: determine canonical status for a transaction
  // Valeurs DB connues : 'approved', 'rejected', 'timeouts', 'canceled', 'tpe'
  const getTransactionStatus = (trans) => {
    if (!trans) return 'other';

    const statusField = (trans.status || '').toString().toLowerCase().trim();

    // Correspondance directe avec les valeurs DB (chemin rapide)
    if (statusField === 'approved') return 'approved';
    if (statusField === 'rejected') return 'rejected';
    if (statusField === 'timeouts' || statusField === 'timeout') return 'timeout';
    if (statusField === 'canceled' || statusField === 'cancelled' || statusField === 'cancledseul') return 'canceled';
    if (statusField === 'tpe' || statusField === 'tpe-unavailable') return 'tpe-unavailable';

    // Détection via trans.result (fallback quand status absent ou générique)
    if (isApprovedTransaction(trans) || isApprovedForCode(trans)) return 'approved';

    const resultRaw = trans.result;
    let resultStr = '';
    if (typeof resultRaw === 'string') resultStr = resultRaw.toLowerCase();
    else {
      try { resultStr = JSON.stringify(resultRaw).toLowerCase(); } catch (e) { resultStr = String(resultRaw).toLowerCase(); }
    }

    if (/tpe.?unavailable|terminal.?unavailable|tpe.?indisponible/.test(resultStr) ||
      (statusField.includes('tpe') && statusField.includes('unavail'))) return 'tpe-unavailable';

    if (statusField.includes('timeout') || /timeout|time out|timed out/.test(resultStr)) return 'timeout';

    if (statusField.includes('cancel') || statusField.startsWith('cancl') ||
      /cancelled|canceled|annul/.test(resultStr)) return 'canceled';

    if (statusField.includes('fail') || statusField.includes('reject') ||
      /reject|declin|refused|refus/.test(resultStr)) return 'rejected';

    if (statusField.includes('pending')) return 'pending';

    return 'other';
  };

  // --- Badge component matching design from your screenshot
  const StatusBadge = ({ statusKey }) => {
    const base =
      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset';
    switch (statusKey) {
      case 'approved':
        return (
          <span className={`${base} bg-emerald-50 text-emerald-800 ring-emerald-200/80`}>
            <FaCheck className="h-2.5 w-2.5" aria-hidden />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className={`${base} bg-rose-50 text-rose-800 ring-rose-200/80`}>
            <FaTimes className="h-2.5 w-2.5" aria-hidden />
            Rejected
          </span>
        );
      case 'canceled':
        return (
          <span className={`${base} bg-slate-100 text-slate-700 ring-slate-200/80`}>
            <FaBan className="h-2.5 w-2.5" aria-hidden />
            Canceled
          </span>
        );
      case 'timeout':
        return (
          <span className={`${base} bg-amber-50 text-amber-900 ring-amber-200/80`}>
            <FaClock className="h-2.5 w-2.5" aria-hidden />
            Timeout
          </span>
        );
      case 'tpe-unavailable':
        return (
          <span className={`${base} bg-violet-50 text-violet-800 ring-violet-200/80`}>
            <FaBroadcastTower className="h-2.5 w-2.5" aria-hidden />
            TPE Unavailable
          </span>
        );
      case 'pending':
        return (
          <span className={`${base} bg-amber-50/90 text-amber-900 ring-amber-200/60`}>
            <FaCircle className="h-1.5 w-1.5 text-amber-500" aria-hidden />
            En attente
          </span>
        );
      default:
        return (
          <span className={`${base} bg-slate-50 text-slate-600 ring-slate-200/70`}>
            <span className="opacity-60">—</span>—
          </span>
        );
    }
  };

  // ResultPreview: uses getTransactionStatus + StatusBadge
  const ResultPreview = ({ result, status, transaction }) => {
    const statKey = getTransactionStatus(transaction || { result, status });
    return (
      <div className="flex items-center">
        <StatusBadge statusKey={statKey} />
      </div>
    );
  };

  const openTransactionHistory = async (trans) => {
    const mongoId = trans?._id;
    const clientId = trans?.clientId;

    if (!mongoId) {
      setHistoryError('Transaction introuvable ');
      setSelectedHistoryTransaction(trans || null);
      setTransactionHistory(null);
      return;
    }

    setSelectedHistoryTransaction(trans);
    setTransactionHistory(null);
    setHistoryError(null);
    setHistoryLoading(true);
    setExpandedAttemptResults({});

    try {
      const headers = getAuthHeaders();
      const fetchOptions = headers
        ? { headers }
        : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

      const params = clientId ? new URLSearchParams({ clientId: String(clientId) }) : null;
      const url = params
        ? `${API_BASE}/transactions/by-id/${encodeURIComponent(mongoId)}?${params.toString()}`
        : `${API_BASE}/transactions/by-id/${encodeURIComponent(mongoId)}`;

      const res = await fetch(url, fetchOptions);

      if (!res.ok) {
        throw new Error((await res.text()) || "Erreur lors de la récupération de l'historique");
      }

      const data = await res.json();
      if (!componentMountedRef.current) return;

      setTransactionHistory(data);
    } catch (err) {
      if (!componentMountedRef.current) return;
      console.error('Fetch transaction history error:', err);
      setHistoryError(err.message || "Erreur lors de la récupération de l'historique");
    } finally {
      if (!componentMountedRef.current) return;
      setHistoryLoading(false);
    }
  };

  const TransactionCard = ({ trans }) => {
    const codeRaw = getResultCode(trans);
    const formatted = formatCodeWithFrench(codeRaw);
    const frenchMsg = getFrenchMessageForCode(codeRaw);
    const approvedForCode = isApprovedForCode(trans);
    return (
      <div className="mb-3 rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
        <div className="flex justify-between items-start gap-2 mb-3">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {archivedOnly && (
              <input
                type="checkbox"
                className="mt-1 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-rose-600 focus:ring-rose-500/30"
                checked={selectedArchiveIds.includes(getArchiveRowId(trans))}
                onChange={() => toggleArchiveSelection(trans)}
                aria-label="Sélectionner la transaction"
              />
            )}
            <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight text-slate-900 mb-0.5 truncate" title={trans.transactionId || trans._id}>
              {trans.transactionId || trans._id}
            </div>
            <div className="text-[11px] text-slate-500 tabular-nums">{formatDateSimple(trans.lastAttemptAt )}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <ResultPreview result={trans.result} status={trans.status} transaction={trans} />
            </div>
            <button
              type="button"
              onClick={() => openTransactionHistory(trans)}
              className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800"
              title="Voir l'historique"
            >
              <FaHistory className="text-sm" />
            </button>
            {trans.result && (
              <button
                type="button"
                onClick={() => setSelectedResult(trans)}
                className="rounded-lg p-2 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-800"
                title="Voir les détails"
              >
                <FaEye className="text-sm" />
              </button>
            )}
            {archivedOnly && (
              <button
                type="button"
                onClick={() => deleteArchivedTransaction(trans)}
                disabled={deletingId === String(trans._id || trans.id)}
                className="rounded-lg p-2 text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-800 disabled:cursor-not-allowed disabled:opacity-50"
                title="Supprimer la transaction archivée"
              >
                {deletingId === String(trans._id || trans.id)
                  ? <FaSync className="animate-spin text-sm" />
                  : <FaTrash className="text-sm" />}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Caisse</span>
            <div className="font-medium text-slate-900">{trans.clientId || '—'}</div>
          </div>
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Magasin</span>
            <div className="font-medium text-slate-900">{getStoreCode(trans)}</div>
          </div>
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">TPE</span>
            <div className="font-medium text-slate-900">{trans.tpeId || '—'}</div>
          </div>
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Montant</span>
            <div className="font-semibold tabular-nums text-slate-900">
              {Number.isFinite(Number(trans.amount))
                ? `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(trans.amount) / 1000)} TND`
                : '-'}
            </div>
          </div>
          <div className="col-span-2 rounded-lg bg-slate-50/80 px-2.5 py-2 ring-1 ring-slate-200/60">
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Code retour</span>
            <div className="font-medium text-slate-900">{approvedForCode ? 'Approuvé' : formatted}</div>
            {!approvedForCode && frenchMsg && <div className="mt-0.5 text-xs text-slate-600">{frenchMsg}</div>}
          </div>
        </div>
      </div>
    );
  };

  // --- client-side filtering logic (applied to the transactions array returned by the server) ---
  const matchesStatusFilter = (trans, selectedStatusLabel) => {
    if (!selectedStatusLabel) return true;
    const expectedKey = labelToStatusKey[selectedStatusLabel];
    if (!expectedKey) return true;
    const transKey = getTransactionStatus(trans);
    return transKey === expectedKey;
  };

  const matchesClientFilter = (trans, caisseId) => {
    if (!caisseId) return true;
    // On retrouve le clientId du device sélectionné pour le comparer à trans.clientId
    const selectedCaisse = caisses.find(c => c._id === caisseId);
    if (!selectedCaisse) return true;
    const clientVal = (trans.clientId || '').toString().toLowerCase();
    return clientVal === (selectedCaisse.clientId || '').toString().toLowerCase();
  };

  const matchesStoreFilter = (trans, storeFilter) => {
    if (!storeFilter) return true;
    const storeVal = (getStoreCode(trans) || '').toString().toLowerCase();
    return storeVal.includes(storeFilter.toString().toLowerCase());
  };

  const displayedTransactions = useMemo(() => {
    return transactions.filter(t =>
      matchesClientFilter(t, filters.caisseId) &&
      matchesStoreFilter(t, filters.storeCode) &&
      // Si statusKey actif → serveur a déjà filtré, on ne double-filtre pas côté client
      (filters.statusKey ? true : matchesStatusFilter(t, filters.status))
    );
  }, [transactions, filters, caisses]);

  /**
   * Récupère toutes les transactions (pagination complète, paquets de 1000).
   * @param {{ ignoreFilters?: boolean }} opts - Si `ignoreFilters: true`, aucun critère de filtre n’est envoyé (toute la base accessible côté API).
   */
  const fetchAllTransactionsPages = async (opts = {}) => {
    const { ignoreFilters = false } = opts;
    const headers = getAuthHeaders();
    const fetchOptions = headers ? { headers } : { credentials: 'include', headers: { 'Content-Type': 'application/json' } };
    const pageSize = 1000;
    let page = 1;
    let totalPagesToFetch = 1;
    const allTransactions = [];

    const f = ignoreFilters ? {} : filtersRef.current || {};
    const selectedCaisse = f.caisseId ? caisses.find((c) => c._id === f.caisseId) : null;
    const clientId = selectedCaisse ? (selectedCaisse.clientId || '') : '';

    while (page <= totalPagesToFetch) {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      if (!ignoreFilters) {
        if (clientId) params.append('clientId', clientId);
        if (f.merchantId) params.append('merchantId', f.merchantId);
        if (f.storeId) params.append('storeId', f.storeId);
        if (f.storeCode) params.append('storeCode', f.storeCode);
        if (f.statusKey) params.append('status', f.statusKey);
        const uf = (f.updatedFrom || '').toString().trim();
        const ut = (f.updatedTo || '').toString().trim();
        if (uf) params.append('updatedFrom', uf);
        if (ut) params.append('updatedTo', ut);
      }
      if (archivedOnlyRef.current) params.append('archivedOnly', 'true');

      const res = await fetch(`${API_BASE}/transactions?${params.toString()}`, fetchOptions);
      if (!res.ok) throw new Error('Erreur lors de la récupération');
      const data = await res.json();

      if (!data?.success || !Array.isArray(data.transactions)) {
        throw new Error('Format de données invalide');
      }

      allTransactions.push(...data.transactions);
      totalPagesToFetch = Number(data.totalPages || totalPagesToFetch || 1);
      page += 1;
    }

    return allTransactions;
  };
  // --- pagination helpers ---
  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      currentPageRef.current = newPage;
    }
  };

  const PageSizeSelector = () => {
    const options = [5, 10, 20, 50, 100, 125];
    return (
      <div className="flex items-center gap-1.5">
        <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Par page</label>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setCurrentPage(1);
            currentPageRef.current = 1;
          }}
          className="block rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // --- Export functions ---
  const exportToExcel = (data, filename) => {
    // Status labels matching the table display
    const statusLabels = {
      'approved': 'Approved',
      'rejected': 'Rejected',
      'canceled': 'Canceled',
      'timeout': 'Timeout',
      'tpe-unavailable': 'TPE Unavailable',
      'pending': 'En attente',
      'other': 'Autre'
    };

    // Prepare data rows
    const excelData = data.map(trans => {
      const amount = Number.isFinite(Number(trans.amount))
        ? (Number(trans.amount) / 1000).toFixed(3)
        : '';
      const status = getTransactionStatus(trans);

      // Format date properly: DD/MM/YYYY HH:MM:SS
      const createdDate = new Date(trans.lastAttemptAt  );
      const dateFormatted = `${createdDate.getDate().toString().padStart(2, '0')}/${(createdDate.getMonth() + 1).toString().padStart(2, '0')}/${createdDate.getFullYear()} ${createdDate.getHours().toString().padStart(2, '0')}:${createdDate.getMinutes().toString().padStart(2, '0')}:${createdDate.getSeconds().toString().padStart(2, '0')}`;

      return {
        'ID Transaction': trans.transactionId || trans._id || '',
        'Caisse': trans.clientId || '',
        'Magasin': getStoreCode(trans),
        'TPE': trans.tpeId || '',
        'Montant (TND)': amount,
        'Code Retour': isApprovedForCode(trans) ? 'Approuvé' : getResultCode(trans),
        'Statut': statusLabels[status] || status,
        'Date': dateFormatted,
        'Numero Carte': trans?.result?.cardNo || '',
        'Auth Code': trans?.result?.authCode || '',
        'Terminal ID': trans?.result?.terminalId || '',
        'AID': trans?.result?.Aid || '',
        'ARQC': trans?.result?.ARQC || '',
        'TUR': trans?.result?.TUR || '',
        'TSI': trans?.result?.TSI || '',
        'App Label': trans?.result?.appLabel || '',
        'Type Transaction': trans?.result?.typeTrans || ''
      };
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      const headers = Object.keys(excelData[0] || {});
      const montantCol = headers.indexOf('Montant (TND)');

      const headerStyle = {
        font: { bold: true, sz: 10, color: { rgb: '334155' } },
        fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { bottom: { style: 'thin', color: { rgb: 'CBD5E1' } } },
      };

      const cellStyle = {
        font: { sz: 10, color: { rgb: '1E293B' } },
        alignment: { vertical: 'center' },
      };

      const amountStyle = {
        ...cellStyle,
        alignment: { horizontal: 'right', vertical: 'center' },
      };

      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const addr = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[addr]) ws[addr].s = headerStyle;
      }

      for (let R = 1; R <= range.e.r; R += 1) {
        for (let C = range.s.c; C <= range.e.c; C += 1) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) continue;
          ws[addr].s = C === montantCol ? amountStyle : cellStyle;
        }
      }

      ws['!autofilter'] = { ref: ws['!ref'] };
      ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
    }

    ws['!cols'] = [
      { wch: 45 },  // ID Transaction
      { wch: 18 },  // Caisse
      { wch: 18 },  // Magasin
      { wch: 18 },  // TPE
      { wch: 18 },  // Montant 
      { wch: 40 },  // Code Retour 
      { wch: 25 },  // Statut
      { wch: 25 },  // Date Création
      { wch: 22 },  // Numero Carte
      { wch: 16 },  // Auth Code
      { wch: 16 },  // Terminal ID
      { wch: 22 },  // AID
      { wch: 22 },  // ARQC
      { wch: 14 },  // TUR
      { wch: 14 },  // TSI
      { wch: 20 },  // App Label
      { wch: 20 }   // Type Transaction
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

    // Generate Excel file and download
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportAll = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const allTransactions = await fetchAllTransactionsPages({ ignoreFilters: true });

      if (allTransactions.length === 0) {
        alert('Aucune transaction à exporter.');
        return;
      }

      exportToExcel(allTransactions, 'transactions_all');
    } catch (err) {
      console.error('Export error:', err);
      alert('Erreur lors de l\'export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  /** Même jeu que le tableau après filtres, mais sur toutes les pages (pas seulement la page affichée). */
  const handleExportFiltered = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const allTransactions = await fetchAllTransactionsPages();
      const f = filtersRef.current || {};
      const filtered = allTransactions.filter(
        (t) =>
          matchesClientFilter(t, f.caisseId) &&
          matchesStoreFilter(t, f.storeCode) &&
          (f.statusKey ? true : matchesStatusFilter(t, f.status)),
      );

      if (filtered.length === 0) {
        alert('Aucune transaction à exporter avec les filtres actuels.');
        return;
      }

      exportToExcel(filtered, 'transactions_filtered');
    } catch (err) {
      console.error('Export error:', err);
      alert('Erreur lors de l\'export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- filters actions ---
  const applyFilters = () => {
    const f = { ...filterInputs };
    // statusKey = valeur DB exacte (pour serveur) ; labelToStatusKey reste pour client-side
    const statusKey = f.status ? (labelToDbStatus[f.status] || '') : '';
    // Retrouver le clientId (champ métier) du device sélectionné via son _id
    const selectedCaisse = f.caisseId ? caisses.find(c => c._id === f.caisseId) : null;
    const clientId = selectedCaisse ? (selectedCaisse.clientId || '') : '';
    const updatedFrom = (f.updatedFrom || '').toString().trim();
    const updatedTo = (f.updatedTo || '').toString().trim();
    setFilters({ ...f, statusKey, clientId, updatedFrom, updatedTo });
    setCurrentPage(1);
    currentPageRef.current = 1;

    // Table : envoyer statusKey (valeur DB) au serveur → filtre sur toutes les pages
    fetchTransactions(1, limitRef.current, {
      clientId,
      merchantId: f.merchantId,
      storeId: f.storeId,
      storeCode: f.storeCode,
      statusKey,
      updatedFrom,
      updatedTo,
    });
  };

  const resetFilters = () => {
    const empty = {
      caisseId: '',
      clientId: '',
      merchantId: '',
      storeId: '',
      storeCode: '',
      status: '',
      statusKey: '',
      updatedFrom: '',
      updatedTo: '',
    };
    setFilterInputs({ caisseId: '', merchantId: '', storeId: '', storeCode: '', status: '', updatedFrom: '', updatedTo: '' });
    setFilters(empty);
    setCurrentPage(1);
    currentPageRef.current = 1;
    fetchTransactions(1, limitRef.current, {});
  };

  const emptyFilterState = {
    caisseId: '',
    clientId: '',
    merchantId: '',
    storeId: '',
    storeCode: '',
    status: '',
    statusKey: '',
    updatedFrom: '',
    updatedTo: '',
  };

  const getArchiveRowId = (trans) => {
    const id = trans?._id || trans?.id;
    return id ? String(id) : '';
  };

  const toggleArchiveSelection = (trans) => {
    const id = getArchiveRowId(trans);
    if (!id) return;
    setSelectedArchiveIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllDisplayedArchives = () => {
    const pageIds = displayedTransactions.map(getArchiveRowId).filter(Boolean);
    if (!pageIds.length) return;
    const allSelected = pageIds.every((id) => selectedArchiveIds.includes(id));
    if (allSelected) {
      setSelectedArchiveIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedArchiveIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const deleteArchivedTransaction = async (trans) => {
    if (!archivedOnlyRef.current) return;
    const id = getArchiveRowId(trans);
    if (!id) {
      alert('Identifiant de transaction introuvable.');
      return;
    }
    const label = trans.transactionId || id;
    if (!window.confirm(`Supprimer définitivement la transaction archivée « ${label} » ?`)) return;

    setDeletingId(String(id));
    try {
      const headers = getAuthHeaders();
      const fetchOptions = {
        method: 'DELETE',
        ...(headers
          ? { headers }
          : { credentials: 'include', headers: { 'Content-Type': 'application/json' } }),
      };
      const base = String(API_BASE || '').replace(/\/$/, '');
      const res = await fetch(`${base}/transactions/archived/by-id/${encodeURIComponent(id)}`, fetchOptions);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || `Erreur HTTP ${res.status}`);
      }
      setSelectedArchiveIds((prev) => prev.filter((x) => x !== id));
      await fetchTransactions(currentPageRef.current, limitRef.current, filtersRef.current);
    } catch (err) {
      console.error('deleteArchivedTransaction error:', err);
      alert(err.message || 'Impossible de supprimer cette transaction archivée.');
    } finally {
      if (componentMountedRef.current) setDeletingId(null);
    }
  };

  const deleteSelectedArchivedTransactions = async () => {
    if (!archivedOnlyRef.current) return;
    const ids = selectedArchiveIds.filter(Boolean);
    if (!ids.length) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} transaction(s) archivée(s) ?`)) return;

    setDeletingId('bulk');
    try {
      const headers = getAuthHeaders() || { 'Content-Type': 'application/json' };
      const fetchOptions = {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ ids }),
        ...(!getAuthHeaders() ? { credentials: 'include' } : {}),
      };
      const base = String(API_BASE || '').replace(/\/$/, '');
      const res = await fetch(`${base}/transactions/archived/by-ids`, fetchOptions);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || `Erreur HTTP ${res.status}`);
      }
      setSelectedArchiveIds([]);
      await fetchTransactions(currentPageRef.current, limitRef.current, filtersRef.current);
    } catch (err) {
      console.error('deleteSelectedArchivedTransactions error:', err);
      alert(err.message || 'Impossible de supprimer les transactions sélectionnées.');
    } finally {
      if (componentMountedRef.current) setDeletingId(null);
    }
  };

  const openArchivedView = () => {
    setFilterInputs({ caisseId: '', merchantId: '', storeId: '', storeCode: '', status: '', updatedFrom: '', updatedTo: '' });
    setFilters(emptyFilterState);
    setSelectedArchiveIds([]);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setArchivedOnly(true);
  };

  const backToMainTable = () => {
    setFilterInputs({ caisseId: '', merchantId: '', storeId: '', storeCode: '', status: '', updatedFrom: '', updatedTo: '' });
    setFilters(emptyFilterState);
    setSelectedArchiveIds([]);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setArchivedOnly(false);
  };

  // --- UI render ---
  return (
    <div className="flex h-screen overflow-hidden bg-slate-100 antialiased">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Navbar />

        {/* Statistiques */}
        <div className="border-b border-slate-200/90 bg-white/95 px-3 py-2.5 backdrop-blur-sm">
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6 md:gap-2">
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
              <FaReceipt className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{globalSummary.total}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
              <FaCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Approved</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{globalSummary.approved}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5">
              <FaTimes className="h-3.5 w-3.5 shrink-0 text-rose-600" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Rejected</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{globalSummary.rejected}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-amber-200/70 bg-amber-50/40 px-2.5 py-2 shadow-sm ring-1 ring-amber-900/5">
              <FaClock className="h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-amber-800/90">Timeouts</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-amber-950">{globalSummary.timeouts}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-slate-50/80 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
              <FaBan className="h-3.5 w-3.5 shrink-0 text-slate-500" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-600">Canceled</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{globalSummary.cancledseul}</p>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-violet-200/70 bg-violet-50/50 px-2.5 py-2 shadow-sm ring-1 ring-violet-900/5">
              <FaBroadcastTower className="h-3.5 w-3.5 shrink-0 text-violet-600" />
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-violet-800/90">TPE Unavailable</p>
                <p className="text-sm font-bold tabular-nums tracking-tight text-violet-950">{globalSummary.tpe}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="border-b border-slate-200/90 bg-white shadow-sm shadow-slate-900/5" ref={topAreaRef}>
          <div className="px-3 py-2.5">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Caisse</label>
                <select
                  value={filterInputs.caisseId}
                  onChange={(e) => setFilterInputs({ ...filterInputs, caisseId: e.target.value })}
                  className="mt-0.5 block w-32 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                  <option value="">Toutes</option>
                  {caisses.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.clientId || c.name || c._id}
                    </option>
                  ))}
                </select>
              </div>

              {canFilterByMerchant && (
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Marchand</label>
                <select
                  value={filterInputs.merchantId}
                  onChange={(e) => setFilterInputs({ ...filterInputs, merchantId: e.target.value, storeId: '', storeCode: '' })}
                  className="mt-0.5 block w-40 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                  <option value="">Tous</option>
                  {merchants.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.name}
                    </option>
                  ))}
                </select>
              </div>
              )}

              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Magasin</label>
                <select
                  value={filterInputs.storeId}
                  onChange={(e) => {
                    const val = e.target.value;
                    const selected = stores.find((s) => (s._id || s.id) === val);
                    setFilterInputs({
                      ...filterInputs,
                      storeId: val,
                      storeCode: selected ? selected.code || '' : '',
                    });
                  }}
                  className="mt-0.5 block w-32 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                  <option value="">Tous</option>
                  {stores
                    .filter((s) => {
                      if (!filterInputs.merchantId) return true;
                      const merchantId = s?.merchantId && typeof s.merchantId === 'object'
                        ? (s.merchantId.id || s.merchantId._id || '')
                        : (s?.merchantId || '');
                      return String(merchantId || '') === String(filterInputs.merchantId);
                    })
                    .map((s) => {
                    const id = s._id || s.id || '';
                    return (
                      <option key={id} value={id}>
                        {s.code ? `${s.code} – ${s.name || ''}` : s.name || id}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Statut</label>
                <select
                  value={filterInputs.status}
                  onChange={(e) => setFilterInputs({ ...filterInputs, status: e.target.value })}
                  className="mt-0.5 block w-44 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                  <option value="">Tous</option>
                  {STATUS_OPTIONS.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">du</label>
                <input
                  type="date"
                  value={filterInputs.updatedFrom}
                  onChange={(e) => setFilterInputs({ ...filterInputs, updatedFrom: e.target.value })}
                  className="mt-0.5 block w-[9.5rem] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium uppercase tracking-wide text-slate-500">au</label>
                <input
                  type="date"
                  value={filterInputs.updatedTo}
                  onChange={(e) => setFilterInputs({ ...filterInputs, updatedTo: e.target.value })}
                  className="mt-0.5 block w-[9.5rem] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>

              <button
                type="button"
                onClick={applyFilters}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                Appliquer
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/25"
              >
                Réinitialiser
              </button>

              {archivedOnly && selectedArchiveIds.length > 0 && (
                <button
                  type="button"
                  onClick={deleteSelectedArchivedTransactions}
                  disabled={deletingId === 'bulk'}
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 shadow-sm transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingId === 'bulk' ? <FaSync className="h-3 w-3 animate-spin" /> : <FaTrash className="h-3 w-3" />}
                  Supprimer ({selectedArchiveIds.length})
                </button>
              )}

              <div className="ml-auto flex min-w-0 flex-wrap items-end justify-end gap-2">
              {canViewArchivedTransactions && (
                archivedOnly ? (
                  <button
                    type="button"
                    onClick={backToMainTable}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <FaArrowLeft className="h-3 w-3" />
                    Retour
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={openArchivedView}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 shadow-sm transition hover:bg-amber-100"
                  >
                    Archives
                  </button>
                )
              )}

              <div className="relative shrink-0" ref={exportMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting || loading}
                  className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                >
                  {exporting ? <FaSync className="h-3 w-3 animate-spin" /> : <FaDownload className="h-3 w-3" />}
                  <span className="hidden sm:inline">Export</span>
                  <FaChevronDown className="h-3 w-3 opacity-80" />
                </button>

                {showExportMenu && (
                  <div className="absolute right-0 z-[80] mt-2 w-56 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-xl border border-slate-200/90 bg-white py-1 shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5">
                    <button
                      type="button"
                      onClick={handleExportAll}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaFileExcel className="shrink-0 text-emerald-600" />
                      <div>
                        <div className="font-semibold text-slate-900">Exporter tout</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={handleExportFiltered}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaFilter className="shrink-0 text-indigo-600" />
                      <div>
                        <div className="font-semibold text-slate-900">Exporter résultat filtré</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  fetchTransactions(currentPage);
                  if (archivedOnly) fetchArchiveFilterOptions();
                  else loadLiveFilterOptions();
                }}
                disabled={loading}
                className="flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                <FaSync className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
              </div>
            </div>
          </div>
        </div>

        {/* modal result */}
        {selectedResult && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
            onClick={() => setSelectedResult(null)}
          >
            <div
              className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl border border-slate-200/80 bg-white p-4 shadow-2xl shadow-slate-900/20 sm:max-h-[80vh] sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <h3 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Détails du résultat</h3>
                <button
                  type="button"
                  onClick={() => setSelectedResult(null)}
                  className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="rounded-xl bg-slate-50/90 p-2 ring-1 ring-slate-200/60">
                {(() => {
                  const res = selectedResult?.result || {};
                  // Liste des champs à afficher dans l'ordre souhaité
                  const fieldsToDisplay = [
                    { key: 'responseCode', label: 'Response Code' },
                    { key: 'result', label: 'Result' },
                    { key: 'cardNo', label: 'Card No' },
                    { key: 'authCode', label: 'Auth Code' },
                    { key: 'terminalId', label: 'Terminal ID' },
                    { key: 'amount', label: 'Amount' },
                    { key: 'dateTime', label: 'Date Time' },
                    { key: 'Aid', label: 'AID' },
                    { key: 'ARQC', label: 'ARQC' },
                    { key: 'TUR', label: 'TUR' },
                    { key: 'TSI', label: 'TSI' },
                    { key: 'appLabel', label: 'App Label' },
                    { key: 'typeTrans', label: 'Type Trans' },
                    { key: 'transactionId', label: 'Transaction ID' }
                  ];

                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {fieldsToDisplay.map(({ key, label }) => {
                        let value = res[key];
                        let hasValue = value !== undefined && value !== null && value !== '';

                        // Special case: if key is 'amount' and value is null/undefined/empty, use selectedResult.amount
                        if (key === 'amount' && !hasValue && selectedResult && selectedResult.amount !== undefined && selectedResult.amount !== null && selectedResult.amount !== '') {
                          value = selectedResult.amount;
                          hasValue = true;
                        }

                        // Pour responseCode, afficher le code complet avec le message français
                        let displayValue = '---';
                        if (hasValue) {
                          if (key === 'responseCode') {
                            const codeStr = String(value);
                            const frenchMsg = getFrenchMessageForCode(codeStr);
                            displayValue = frenchMsg ? `${codeStr} - ${frenchMsg}` : codeStr;
                          } else {
                            displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                          }
                        }

                        return (
                          <div
                            key={key}
                            className={`flex flex-col rounded-lg border border-slate-200/80 bg-white px-2 py-1.5 shadow-sm ${key === 'responseCode' ? 'col-span-2 sm:col-span-3' : ''
                              }`}
                          >
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
                            <span className="break-all font-mono text-xs text-slate-900">{displayValue}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* modal history */}
        {selectedHistoryTransaction && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
            onClick={() => {
              setSelectedHistoryTransaction(null);
              setTransactionHistory(null);
              setHistoryError(null);
              setHistoryLoading(false);
              setExpandedAttemptResults({});
            }}
          >
            <div
              className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/15 sm:max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 border-b border-blue-900/10 bg-blue-50 px-4 py-4 sm:px-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                  
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Historique de la transaction</h3>
                      <p className="mt-1 truncate font-mono text-xs text-slate-500">
                        Transaction {selectedHistoryTransaction.transactionId || selectedHistoryTransaction._id}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedHistoryTransaction(null);
                      setTransactionHistory(null);
                      setHistoryError(null);
                      setHistoryLoading(false);
                      setExpandedAttemptResults({});
                    }}
                    className="rounded-lg p-2 text-slate-400 transition hover:bg-white hover:text-slate-700"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
                {historyLoading && (
                  <div className="flex h-48 items-center justify-center">
                    <div className="text-center">
                      <FaSync className="mx-auto mb-3 h-6 w-6 animate-spin text-blue-700" />
                      <p className="text-sm font-medium text-slate-600">Chargement de l'historique…</p>
                    </div>
                  </div>
                )}

                {!historyLoading && historyError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-5 text-center">
                    <FaTimes className="mx-auto mb-2 text-2xl text-rose-500" />
                    <p className="text-sm font-medium text-rose-800">{historyError}</p>
                  </div>
                )}

                {!historyLoading && !historyError && (
                  <>
                    {(() => {
                      const historyView = getHistoryPayload(transactionHistory, selectedHistoryTransaction);
                      return (
                        <>
                          <div className="mb-6 overflow-hidden rounded-xl border border-slate-200">
                            <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Statut global</div>
                                <div className="mt-2">
                                  <StatusBadge statusKey={getTransactionStatus(historyView.statusSource)} />
                                </div>
                              </div>
                              <div className="text-left text-xs text-slate-500 sm:text-right">
                                <div>Dernière tentative: {historyView.currentAttemptNumber || '—'}</div>
                                <div>Le: {formatDateSimple(historyView.lastAttemptAt)}</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                              <div className="px-4 py-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Caisse</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {historyView.clientId}
                                </div>
                              </div>
                              <div className="px-4 py-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Montant courant</div>
                                <div className="mt-1 text-sm font-semibold tabular-nums text-slate-900">
                                  {formatAmount(historyView.amount, historyView.currency)}
                                </div>
                              </div>
                              <div className="px-4 py-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Tentatives</div>
                                <div className="mt-1 text-sm font-semibold text-slate-900">
                                  {historyView.attemptCount}
                                </div>
                              </div>
                            </div>

                            {historyView.result && (
                              <div className="border-t border-slate-100 px-4 py-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Résultat final</div>
                                <div className="mt-1 text-sm font-medium leading-6 text-slate-900">
                                  {historyView.result.result || formatResultDisplay(historyView.result)}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-3">
                            {historyView.attempts.map((attempt) => {
                              const attemptResult = attempt?.result?.result || formatResultDisplay(attempt?.result);
                              const attemptResultFields = getResultFieldsForDisplay(attempt?.result, attempt?.amount);
                              const attemptKey = attempt._id || `${attempt.transactionId}-${attempt.attemptNumber}`;
                              const isAttemptExpanded = Boolean(expandedAttemptResults[attemptKey]);
                              return (
                                <div
                                  key={attemptKey}
                                  className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                >
                                  <div className="flex border-l-[3px] border-blue-800">
                                    <div className="min-w-0 flex-1 px-4 py-3">
                                      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                                          <span className="text-sm font-semibold text-slate-900">
                                            Tentative {attempt.attemptNumber || '—'}
                                          </span>
                                          <StatusBadge statusKey={getTransactionStatus(attempt)} />
                                        </div>
                                        <div className="text-left text-xs text-slate-500 sm:text-right">
                                          <div>Date: {formatDateSimple(attempt.completedAt || attempt.updatedAt)}</div>
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
                                        <div>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Montant</div>
                                          <div className="mt-0.5 text-sm font-medium tabular-nums text-slate-900">
                                            {formatAmount(attempt.amount, attempt.currency)}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">TPE</div>
                                          <div className="mt-0.5 text-sm font-medium text-slate-900">{attempt.tpeId || '—'}</div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Code retour</div>
                                          <div className="mt-0.5 text-sm font-medium text-slate-900">
                                            {isApprovedForCode(attempt) ? 'Approuvé' : formatCodeWithFrench(getResultCode(attempt))}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Résultat</div>
                                          <div className="mt-0.5 text-sm font-medium leading-6 text-slate-900">{attemptResult}</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="border-t border-slate-100">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedAttemptResults((prev) => ({
                                          ...prev,
                                          [attemptKey]: !prev[attemptKey],
                                        }))
                                      }
                                      className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-blue-50/60"
                                    >
                                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                        Détail du résultat
                                      </span>
                                      {isAttemptExpanded ? (
                                        <FaChevronDown className="h-3 w-3 text-blue-800" />
                                      ) : (
                                        <FaChevronRight className="h-3 w-3 text-slate-400" />
                                      )}
                                    </button>

                                    {isAttemptExpanded && (
                                      <div className="grid grid-cols-1 gap-2 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:grid-cols-2 xl:grid-cols-3">
                                        {attemptResultFields.map(({ key, label, displayValue, isWide }) => (
                                          <div
                                            key={`${attemptKey}-${key}`}
                                            className={`flex flex-col px-0 py-1 ${isWide ? 'sm:col-span-2 xl:col-span-3' : ''
                                              }`}
                                          >
                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
                                            <span className="break-all font-mono text-xs text-slate-900">{displayValue}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {historyView.attempts.length === 0 ? (
                              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                                Aucun historique de tentative disponible.
                              </div>
                            ) : null}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <main className="min-h-0 flex-1 overflow-hidden">
          {loading && (
            <div className="flex h-64 items-center justify-center px-4">
              <div className="text-center">
                <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                <p className="text-sm font-medium text-slate-600">Chargement des transactions…</p>
              </div>
            </div>
          )}
          {error && (
            <div className="m-4 rounded-xl border border-rose-200 bg-rose-50/90 p-4 text-center shadow-sm sm:p-6">
              <FaTimes className="mx-auto mb-2 text-2xl text-rose-500" />
              <p className="text-sm font-medium text-rose-800 sm:text-base">Erreur : {error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-200/60">
              {/* DESKTOP */}
              <div className="hidden min-h-0 flex-1 overflow-hidden md:flex md:flex-col">
                {displayedTransactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <FaSearch className="mx-auto mb-2 h-8 w-8 opacity-40" />
                    <div className="text-sm font-medium text-slate-600">
                      {archivedOnly ? 'Aucune transaction archivée trouvée' : 'Aucune transaction trouvée'}
                    </div>
                  </div>
                ) : (
                  <div className="min-h-0 flex-1 overflow-auto">
                    <table className="min-w-full table-fixed">
                      <thead className="sticky top-0 z-10 border-b border-blue-900/20 bg-blue-50">
                        <tr>
                          {archivedOnly && (
                            <th className="w-10 px-3 py-2 text-left">
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500/30"
                                checked={
                                  displayedTransactions.length > 0 &&
                                  displayedTransactions.every((t) => selectedArchiveIds.includes(getArchiveRowId(t)))
                                }
                                onChange={toggleSelectAllDisplayedArchives}
                                aria-label="Tout sélectionner"
                              />
                            </th>
                          )}
                          <th className="w-44 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">
                            ID Transaction
                          </th>
                          <th className="w-20 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Caisse</th>
                          <th className="w-20 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Magasin</th>
                          <th className="w-20 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">TPE</th>
                          <th className="w-24 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Montant</th>
                          <th className="w-24 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Code retour</th>
                          <th className="w-28 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Statut</th>
                          <th className="w-32 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Date</th>
                          <th className="w-24 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {displayedTransactions.map((trans) => {
                          const codeRaw = getResultCode(trans);
                          const formattedCode = formatCodeWithFrench(codeRaw);
                          const approvedForCode = isApprovedForCode(trans);
                          return (
                            <tr key={trans.transactionId || trans._id} className="transition-colors hover:bg-slate-50/90">
                              {archivedOnly && (
                                <td className="w-10 px-3 py-2 align-top">
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500/30"
                                    checked={selectedArchiveIds.includes(getArchiveRowId(trans))}
                                    onChange={() => toggleArchiveSelection(trans)}
                                    aria-label="Sélectionner la transaction"
                                  />
                                </td>
                              )}
                              <td className="w-44 whitespace-normal break-words px-3 py-2 align-top">
                                <div className="truncate text-xs font-medium text-slate-900" title={trans.transactionId || trans._id}>
                                  {trans.transactionId || trans._id}
                                </div>
                              </td>
                              <td className="w-20 whitespace-normal break-words px-3 py-2 align-top">
                                <span className="text-xs text-slate-800">{trans.clientId || '—'}</span>
                              </td>
                              <td className="w-20 whitespace-normal break-words px-3 py-2 align-top">
                                <span className="text-xs text-slate-800">{getStoreCode(trans)}</span>
                              </td>
                              <td className="w-20 whitespace-normal break-words px-3 py-2 align-top">
                                <div className="flex items-center gap-1">
                                  <FaCreditCard className="h-3 w-3 shrink-0 text-indigo-500" />
                                  <span className="text-xs text-slate-800">{trans.tpeId || '—'}</span>
                                </div>
                              </td>
                              <td className="w-24 whitespace-normal break-words px-3 py-2 align-top">
                                <div className="text-xs font-semibold tabular-nums text-slate-900">
                                  {Number.isFinite(Number(trans.amount))
                                    ? `${new Intl.NumberFormat('fr-TN', {
                                      minimumFractionDigits: 3,
                                      maximumFractionDigits: 3,
                                    }).format(Number(trans.amount) / 1000)} TND`
                                    : '-'}
                                </div>
                              </td>
                              <td className="w-24 whitespace-normal break-words px-3 py-2 align-top">
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800 ring-1 ring-slate-200/80">
                                  {approvedForCode ? 'Approuvé' : formattedCode}
                                </span>
                              </td>
                              <td className="w-28 whitespace-normal break-words px-3 py-2 align-top">
                                <ResultPreview result={trans.result} status={trans.status} transaction={trans} />
                              </td>
                              <td className="w-32 whitespace-normal break-words px-3 py-2 align-top text-xs tabular-nums text-slate-500">
                                {formatDateSimple(trans.lastAttemptAt )}
                              </td>
                              <td className="w-24 whitespace-normal break-words px-3 py-2 align-top">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => openTransactionHistory(trans)}
                                    className="rounded-md p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
                                    title="Voir l'historique"
                                  >
                                    <FaHistory className="text-xs" />
                                  </button>
                                  {trans.result && (
                                    <button
                                      type="button"
                                      onClick={() => setSelectedResult(trans)}
                                      className="rounded-md p-1.5 text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-800"
                                      title="Voir les détails"
                                    >
                                      <FaEye className="text-xs" />
                                    </button>
                                  )}
                                  {archivedOnly && (
                                    <button
                                      type="button"
                                      onClick={() => deleteArchivedTransaction(trans)}
                                      disabled={deletingId === String(trans._id || trans.id)}
                                      className="rounded-md p-1.5 text-rose-600 transition hover:bg-rose-50 hover:text-rose-800 disabled:cursor-not-allowed disabled:opacity-50"
                                      title="Supprimer la transaction archivée"
                                    >
                                      {deletingId === String(trans._id || trans.id)
                                        ? <FaSync className="animate-spin text-xs" />
                                        : <FaTrash className="text-xs" />}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* MOBILE */}
              <div className="flex min-h-0 flex-1 overflow-hidden md:hidden">
                {displayedTransactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <FaSearch className="mx-auto mb-2 h-8 w-8 opacity-40" />
                    <div className="text-sm font-medium text-slate-600">
                      {archivedOnly ? 'Aucune transaction archivée trouvée' : 'Aucune transaction trouvée'}
                    </div>
                  </div>
                ) : (
                  <div className="h-full w-full overflow-y-auto p-4">
                    {displayedTransactions.map((trans) => (
                      <TransactionCard key={trans.transactionId || trans._id} trans={trans} />
                    ))}
                  </div>
                )}
              </div>

              <div ref={bottomAreaRef} className="shrink-0 border-t border-slate-200/90 bg-slate-50/90 px-4 py-3 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="hidden text-sm tabular-nums text-slate-600 sm:block">
                    Affichage de {totalCount === 0 ? 0 : (currentPage - 1) * limit + 1} à {Math.min(currentPage * limit, totalCount)} sur {totalCount}
                  </div>
                  <div className="flex items-center gap-3">
                    <PageSizeSelector />
                    <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <FaChevronLeft className="text-xs" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <FaChevronRight className="text-xs" />
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
